import { useMemo, useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type {
  UiPaletteStep,
  UiTemplateItem,
} from "@/features/template-builder/queries";
import { templateBuilderQueries } from "@/features/template-builder/queries";
import { templateBuilderMutations } from "@/features/template-builder/mutations";
import { useSession } from "@/platform/auth";
import { useRecordPermissions, useTenant } from "@/domains/tenant/hooks";
import { useInsertStepAtPosition } from "./use-insert-step-at-position";
import { insertCachedTemplateStep } from "@/features/template-builder/queries/cache";
import { isOrdinaryVariable } from "@/domains/templates/subanalysis";
import type { CreateSubanalysisStepInput } from "@/domains/templates/subanalysis.types";
import type { TemplateWithSettings } from "@/domains/templates/types";

export type PaletteSelection =
  | { kind: "blank" }
  | { kind: "step"; templateId: string; stepId: string }
  | { kind: "template"; templateId: string };

export type PaletteTemplateGroup = {
  template: UiTemplateItem;
  steps: UiPaletteStep[];
};

type Placement = CreateSubanalysisStepInput["placement"];

export function useSectionPalette({
  analysisId,
  stepsCount,
}: {
  analysisId: string;
  stepsCount: number;
}) {
  const { user } = useSession();
  const { data: tenantData } = useTenant();
  const { canEdit } = useRecordPermissions();
  const queryClient = useQueryClient();
  const submitGuard = useRef(false);
  const { mutateAsync: insertAtPosition, isPending: isInsertingAtPosition } =
    useInsertStepAtPosition();
  const { data: targetTemplate } = useQuery(
    templateBuilderQueries.detail(analysisId),
  );
  const { data: targetTemplateSteps } = useQuery(
    templateBuilderQueries.steps(analysisId),
  );

  const { mutateAsync: insertTopMutation, isPending: isInsertingTop } =
    useMutation({
      mutationFn: (params: { templateId: string; templateStepId?: string }) =>
        templateBuilderMutations.createTemplateStepTop(params),
      onSuccess: (step, { templateId }) =>
        insertCachedTemplateStep(queryClient, templateId, step),
    });
  const { mutateAsync: insertBottomMutation, isPending: isInsertingBottom } =
    useMutation({
      mutationFn: (params: { templateId: string; templateStepId?: string }) =>
        templateBuilderMutations.createTemplateStepBottom(params),
      onSuccess: (step, { templateId }) =>
        insertCachedTemplateStep(queryClient, templateId, step),
    });
  const {
    mutateAsync: insertTemplateMutation,
    isPending: isInsertingTemplate,
  } = useMutation({
    mutationFn: (definition: CreateSubanalysisStepInput) =>
      templateBuilderMutations.createSubanalysisStep({
        templateId: analysisId,
        definition,
      }),
    onSuccess: (step) => {
      insertCachedTemplateStep(queryClient, analysisId, step);
      void queryClient.invalidateQueries({
        queryKey: templateBuilderQueries.steps(analysisId).queryKey,
      });
    },
  });

  const [search, setSearch] = useState("");
  const [showGlobal, setShowGlobal] = useState(true);
  const [openTemplates, setOpenTemplates] = useState<Record<string, boolean>>(
    {},
  );
  const [selection, setSelection] = useState<PaletteSelection | null>(null);
  const [isPreparing, setIsPreparing] = useState(false);
  const { data: templates, isLoading: isLoadingTemplates } = useQuery(
    templateBuilderQueries.list(
      { search: "", showGlobal },
      tenantData?.tenant.tenant_id,
    ),
  );
  const orderedTemplates = useMemo(() => {
    const other = (templates ?? []).filter(
      (template) => template.id !== analysisId,
    );
    return [
      ...other.filter((template) => !template.isGlobal),
      ...other.filter((template) => template.isGlobal),
    ];
  }, [templates, analysisId]);
  const templateIds = useMemo(
    () => orderedTemplates.map((template) => template.id),
    [orderedTemplates],
  );
  const { data: paletteSteps, isLoading: isLoadingSteps } = useQuery(
    templateBuilderQueries.paletteSteps(templateIds),
  );
  const normalizedSearch = search.trim().toLowerCase();
  const grouped = useMemo(
    (): PaletteTemplateGroup[] =>
      orderedTemplates.flatMap((template) => {
        const templateMatch = template.name
          .toLowerCase()
          .includes(normalizedSearch);
        const steps = (paletteSteps ?? [])
          .filter(
            (step) =>
              step.templateId === template.id &&
              (templateMatch ||
                step.name.toLowerCase().includes(normalizedSearch)),
          )
          .sort((left, right) => left.order - right.order);
        return templateMatch || steps.length ? [{ template, steps }] : [];
      }),
    [orderedTemplates, paletteSteps, normalizedSearch],
  );

  const selectedTemplate =
    selection?.kind === "template"
      ? orderedTemplates.find(
          (template) => template.id === selection.templateId,
        )
      : undefined;
  const selectedStep =
    selection?.kind === "step"
      ? (paletteSteps ?? []).find(
          (step) =>
            step.templateId === selection.templateId &&
            step.id === selection.stepId,
        )
      : undefined;
  const orderedCurrentSteps = (targetTemplateSteps ?? [])
    .slice()
    .sort((left, right) => left.order - right.order);
  const resolveInsertOrder = (position: number) =>
    position <= orderedCurrentSteps.length
      ? orderedCurrentSteps[position - 1]!.order
      : (orderedCurrentSteps.at(-1)?.order ?? 0) + 1;
  const isInserting =
    isPreparing ||
    isInsertingAtPosition ||
    isInsertingTop ||
    isInsertingBottom ||
    isInsertingTemplate;
  const canInsert = Boolean(
    user &&
    tenantData &&
    targetTemplate &&
    canEdit(targetTemplate.record_tenant_id),
  );
  const insertDisabled =
    isInserting ||
    !canInsert ||
    !selection ||
    (selection.kind === "template" && !selectedTemplate) ||
    (selection.kind === "step" && !selectedStep);

  const dispatch = async (placement: Placement): Promise<unknown> => {
    if (submitGuard.current || isInserting) return;
    if (!canInsert)
      throw new Error("You do not have permission to edit this template.");
    if (!selection) return;
    if (
      placement.kind === "position" &&
      (!Number.isInteger(placement.position) ||
        placement.position < 1 ||
        placement.position > orderedCurrentSteps.length + 1)
    )
      throw new Error(
        "That position is no longer available. Choose a current position.",
      );
    submitGuard.current = true;
    setIsPreparing(true);
    try {
      if (selection.kind === "template") {
        const template = orderedTemplates.find(
          (item) => item.id === selection.templateId,
        );
        if (!template)
          throw new Error("The selected template is no longer available.");
        const child = (await queryClient.ensureQueryData(
          templateBuilderQueries.detail(selection.templateId),
        )) as TemplateWithSettings;
        const inputs = (child.variables ?? [])
          .filter(
            (variable) =>
              isOrdinaryVariable(variable) &&
              variable.variable_kind === "input" &&
              variable.analysis_id === selection.templateId &&
              variable.analysis_step_id === null &&
              variable.parent_variable_id === null,
          )
          .map((variable) => ({
            binding_target_variable_id: variable.variable_id,
            binding_kind:
              variable.variable_default === null
                ? ("unbound" as const)
                : ("target_default" as const),
          }));
        return await insertTemplateMutation({
          display_name: "Execute " + template.name,
          placement,
          call: {
            binding_template_id: selection.templateId,
            binding_source_step_id: null,
            binding_source_variable_id: null,
            binding_path: "",
          },
          inputs,
        });
      }
      const templateStepId =
        selection.kind === "step" ? selectedStep?.id : undefined;
      if (selection.kind === "step" && !templateStepId)
        throw new Error("The selected section is no longer available.");
      const params = { templateId: analysisId, templateStepId };
      if (placement.kind === "top") return await insertTopMutation(params);
      if (placement.kind === "bottom")
        return await insertBottomMutation(params);
      return await insertAtPosition({
        ...params,
        position: resolveInsertOrder(placement.position),
      });
    } finally {
      submitGuard.current = false;
      setIsPreparing(false);
    }
  };

  return {
    search,
    setSearch,
    showGlobal,
    setShowGlobal,
    grouped,
    isLoading: isLoadingTemplates || isLoadingSteps,
    hasResults: grouped.length > 0,
    openTemplates,
    setOpenTemplates,
    selection,
    setSelection,
    insertTop: () => dispatch({ kind: "top" }),
    insertBottom: () => dispatch({ kind: "bottom" }),
    insertAt: (position: number) => dispatch({ kind: "position", position }),
    insertDisabled,
    isInserting,
    clearSelection: () => setSelection(null),
    selectedStepName:
      selection?.kind === "blank"
        ? "New Section"
        : selection?.kind === "template"
          ? "Execute " + (selectedTemplate?.name ?? "template")
          : (selectedStep?.name ?? "section"),
    stepsCount,
  };
}
